import { formatPropertyAddress } from '../utils/propertyAddress';
import PropertyThumbnail from './PropertyThumbnail';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropertySwipeHint from './PropertySwipeHint';
import './LegacyMobilePropertyBrowser.css';

// Reuse the site's search, detail, and favorite handlers while leaving its desktop chat intact.
export default function LegacyMobilePropertyBrowser({ message, pageSize, onMore, onRestart, onOpen, onSave, favorites, images, paused }) {
  const [mobile, setMobile] = useState(false);
  const [view, setView] = useState('chat');
  const workspaceRef = useRef(null);
  const resultsRef = useRef(null);
  const railRef = useRef(null);
  const revealRef = useRef(null);
  const visible = message?.properties?.slice(0, ((message.currentPage || 0) + 1) * pageSize) || [];
  const remaining = message ? Math.max(0, message.properties.length - visible.length) + (message.hasMore ? pageSize : 0) : 0;
  const move = next => {
    setView(next);
    if (next === 'chat') revealRef.current = null;
    workspaceRef.current?.scrollTo({ left: next === 'chat' ? 0 : workspaceRef.current.clientWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  useEffect(() => {
    const media = window.matchMedia('(max-width: 820px)');
    const update = () => setMobile(media.matches);
    update(); media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  useEffect(() => { setView('chat'); revealRef.current = null; }, [message?.searchId]);
  useEffect(() => {
    if (!mobile || !visible.length || paused) return undefined;
    let start;
    const begin = event => { start = event.target.closest('.chat-page') && !event.target.closest('input,textarea,select') ? event.touches[0] : null; };
    const end = event => {
      const last = event.changedTouches[0];
      if (start && last && workspaceRef.current?.scrollLeft < 20 && Math.abs(last.clientX - start.clientX) > 70 && Math.abs(last.clientX - start.clientX) > Math.abs(last.clientY - start.clientY) * 1.5) move('results');
      start = null;
    };
    document.addEventListener('touchstart', begin, { passive: true });
    document.addEventListener('touchend', end, { passive: true });
    return () => { document.removeEventListener('touchstart', begin); document.removeEventListener('touchend', end); };
  }, [mobile, visible.length, paused]);
  useEffect(() => {
    const index = revealRef.current;
    if (index == null || message?.loadingMore) return undefined;
    if (message?.pageError) { revealRef.current = null; return undefined; }
    const card = railRef.current?.querySelector(`[data-property-index="${index}"]`);
    if (!card) { revealRef.current = null; return undefined; }
    const frame = requestAnimationFrame(() => {
      if (revealRef.current !== index) return;
      revealRef.current = null;
      const rail = railRef.current;
      if (!rail || workspaceRef.current?.scrollLeft < 20) return;
      if (document.activeElement?.closest('.assistant-results-load-more')) card.querySelector('button')?.focus({ preventScroll: true });
      rail.scrollTo({ left: rail.scrollLeft + card.getBoundingClientRect().left - rail.getBoundingClientRect().left, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [message]);
  if (!mobile || !visible.length) return null;
  return createPortal(<>
    <div className="legacy-mobile-properties" style={paused ? { display: 'none' } : undefined}>
      <nav className="assistant-mobile-workspace-tabs" aria-label="Switch between chat and property results">
        <button type="button" className={view === 'chat' ? 'is-active' : ''} aria-pressed={view === 'chat'} onClick={() => move('chat')}>← Chat</button>
        <button type="button" className={view === 'results' ? 'is-active' : ''} aria-pressed={view === 'results'} onClick={() => move('results')}>Homes <span>{visible.length}</span></button>
      </nav>
      <div className="assistant-workspace legacy-mobile-workspace" key={message.searchId} ref={workspaceRef} onScroll={() => setView(workspaceRef.current.scrollLeft > workspaceRef.current.clientWidth / 2 ? 'results' : 'chat')}>
        <div className="legacy-mobile-conversation" aria-hidden="true" />
        <section className="assistant-results-panel" ref={resultsRef} aria-label={`${visible.length} matching properties`}>
          <button type="button" className="assistant-mobile-back-to-chat" onClick={() => move('chat')}>← Back to chat</button>
          <header><small>LIVE SEARCH RESULTS</small><h2>{visible.length} homes to explore</h2><p>Swipe through homes. Tap a property to see its details.</p></header>
          <div className="assistant-property-grid" ref={railRef}>
            {visible.map((property, index) => {
              const key = property.listing_key || property.id;
              const address = formatPropertyAddress(property);
              const photo = images[property.listing_key] || property.first_photo_url || property.image_url || property.photo_url;
              const price = Number(property.current_price || property.list_price || property.price);
              return <article className="assistant-property-card" data-property-index={index} key={key}>
                <button type="button" className="assistant-property-media" onClick={() => onOpen(property)} aria-label={`View details for ${address}`}><PropertyThumbnail listingKey={property.listing_key} src={photo} alt={address} /></button>
                <button type="button" className="assistant-property-save" aria-label={`Save ${address}`} onClick={() => onSave(property)}>{favorites.has(key) ? 'Saved' : 'Save'}</button>
                <div className="assistant-property-content"><strong>{Number.isFinite(price) ? price.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : 'Price available on request'}</strong><p>{address}</p><p>{property.bedrooms_total || property.bedrooms || '—'} beds · {property.bathrooms_total || property.bathrooms || '—'} baths</p><button type="button" onClick={() => onOpen(property)}>View residence →</button></div>
              </article>;
            })}
            {remaining > 0 && <div className="assistant-results-load-more" aria-busy={Boolean(message.loadingMore)}>
              <p>{visible.length} homes shown</p>
              {message.pageError && <p className="assistant-results-page-error" role="alert">{message.pageError}</p>}
              <button type="button" className="assistant-results-more" disabled={message.loadingMore} onClick={() => { if (message.restartSearch) onRestart(); else { revealRef.current = visible.length; onMore(); } }}><small>CONTINUE BROWSING</small><strong>{message.loadingMore ? 'Loading homes…' : message.restartSearch ? 'Restart search' : message.pageError ? 'Try again' : `See ${Math.min(pageSize, remaining)} more properties`}</strong><span aria-hidden="true">→</span></button>
            </div>}
          </div>
        </section>
      </div>
    </div>
    <PropertySwipeHint key={message.searchId} workspaceRef={workspaceRef} resultsRef={resultsRef} paused={paused} />
  </>, document.body);
}
